import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  FormLabel,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

import versionService from '../../../services/versionService.js';
import analyticsService from '../../../services/analyticsService.js';

const PURPLE = '#6C4DFF';

function DuplicateVersionModal({ open, onClose, rootScriptId, currentVersion, nextMinor, onDuplicated, onProRequired }) {
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const currentDisplay = currentVersion
    ? `v${currentVersion.version_major} · ${currentVersion.version_color}`
    : '';

  const handleClose = () => {
    if (saving) return;
    setLabel('');
    onClose();
  };

  const handleDuplicate = async () => {
    if (saving) return;
    setSaving(true);
    const startedAt = Date.now();
    console.log('[versioning] duplicate: request start', { rootScriptId, label: label.trim() });
    try {
      const version = await versionService.duplicate(rootScriptId, label.trim());
      console.log('[versioning] duplicate: response ok', {
        rootScriptId,
        version,
        ms: Date.now() - startedAt,
      });
      analyticsService.trackEvent('version_duplicated', { root_script_id: rootScriptId });
      setLabel('');
      onDuplicated(version);
    } catch (err) {
      console.error('[versioning] duplicate: request failed', {
        rootScriptId,
        code: err.code,
        message: err.message,
        ms: Date.now() - startedAt,
      });
      if (err.code === 'PRO_REQUIRED') {
        onClose();
        onProRequired();
      } else {
        toast.error('Unable to duplicate this version. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, padding: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box className="fullWidthContainer">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Duplicate {currentDisplay}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Creates v{currentVersion?.version_major}.{nextMinor} — an editable copy of the{' '}
            {currentVersion?.version_color} draft. Same script, same shotlist.
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={saving} sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 600, color: 'text.primary' }}>
          Label (optional)
        </FormLabel>
        <TextField
          placeholder="Stunt unit, Director's cut, Plan B…"
          fullWidth
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          inputProps={{ maxLength: 255 }}
          disabled={saving}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
          sx={{
            mr: 1.5,
            borderRadius: '999px',
            textTransform: 'none',
            borderColor: '#D0CCFF',
            color: '#666',
            backgroundColor: '#F9F8FF',
            '&:hover': { backgroundColor: '#F1EEFF', borderColor: PURPLE },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDuplicate}
          disabled={saving}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            px: 4,
            backgroundColor: PURPLE,
            '&:hover': { backgroundColor: '#5c3ae0' },
            '&.Mui-disabled': { backgroundColor: PURPLE, opacity: 0.7, color: '#fff' },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              Duplicating…
            </>
          ) : (
            'Duplicate'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DuplicateVersionModal;
